import { getDashboardStats, getMonthlyTicketCounts, type DashboardStatsData, type MonthlyCountItem } from '@/api/dashboard-api';
import FilterIcon from '@/assets/icons/filter.svg';
import { DBStatusCards } from '@/components/dashboard-statusCard/DashboardStatusCard';
import { DashboardFilterModal, type DashboardDateFilter } from '@/components/filter/DashboardFilterModal';
import { FilterButton } from '@/components/index';
import { AgingConcernLineChart } from '@/components/mui-charts/AgingConcernLineChart';
import { AverageDayOfCompletionBar } from '@/components/mui-charts/AverageDayOfCompletionBar';
import { AverageTicketsPie } from '@/components/mui-charts/AverageTicketsPie';
import { useAverageDaysCompletion } from '@/hooks/useAverageDaysCompletion';
import { usePieChart } from '@/hooks/usePieChart';
import dayjs from '@/utilities/dayjs-config';
import { Box, Grid } from '@mui/material';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface DashboardProps {}

export const Dashboard: FC<DashboardProps> = () => {
  const [loading, setLoading] = useState(true);
  const [chartsReady, setChartsReady] = useState(false);
  const [stats, setStats] = useState<DashboardStatsData | null>(null);
  const [statsLoading, setStatsLoading] = useState<boolean>(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyCountItem[]>([]);
  const [monthlyLoading, setMonthlyLoading] = useState<boolean>(false);
  const [monthlyError, setMonthlyError] = useState<string | null>(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false)

  const today = dayjs()
  const [dateFilter, setDateFilter] = useState<DashboardDateFilter>({
    mode: 'year',
    year: today.year(),
    sinceDate: null,
    untilDate: null,
  })

  const formattedDateRange = useMemo(() => {
    if (dateFilter.mode === 'year' && dateFilter.year) {
      return `${dateFilter.year}`
    }

    if (dateFilter.mode === 'custom' && dateFilter.sinceDate && dateFilter.untilDate) {
      const since = dayjs(dateFilter.sinceDate).format('MMM YYYY')
      const until = dayjs(dateFilter.untilDate).format('MMM YYYY')
      return `${since} - ${until}`
    }

    return 'All Time'
  }, [dateFilter])

  const handleApplyFilter = useCallback((filters: DashboardDateFilter) => {
    setDateFilter(filters)
  }, [])

  // Get user from localStorage - memoized to prevent infinite re-renders
  const { userId, roleName } = useMemo(() => {
    try {
      const userIdString = localStorage.getItem('userId');
      const roleString = localStorage.getItem('role');
  
      const userId = userIdString ? JSON.parse(userIdString) : null;
      const roleName = roleString ? JSON.parse(roleString) : null;

      console.log('Dashboard localStorage values:', { userIdString, roleString, userId, roleName });
  
      return {
        userId,
        roleName,
      };
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      return { userId: null, roleName: null };
    }
  }, []);

  const dashboardQueryParams = useMemo(() => ({
    userId,
    roleName,
    year: dateFilter.mode === 'year' ? dateFilter.year ?? undefined : undefined,
    sinceDate: dateFilter.mode === 'custom' ? dateFilter.sinceDate ?? undefined : undefined,
    untilDate: dateFilter.mode === 'custom' ? dateFilter.untilDate ?? undefined : undefined,
  }), [userId, roleName, dateFilter.mode, dateFilter.year, dateFilter.sinceDate, dateFilter.untilDate])
  
  // Fetch dashboard status stats
  useEffect(() => {
    if (!userId) return;
    setStatsLoading(true);
    setStatsError(null);
    const controller = new AbortController();
    const fetchStats = async () => {
      try {
        const result = await getDashboardStats(dashboardQueryParams, { signal: controller.signal });
        setStats(result);
      } catch (err: any) {
        if (err?.name === 'CanceledError') return; // axios cancellation
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          setStatsError('Unauthorized to load dashboard stats. Please sign in again.');
        } else {
          setStatsError(err?.response?.data?.message || err?.message || 'Failed to load dashboard stats');
        }
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
    return () => controller.abort();
  }, [userId, dashboardQueryParams]);

  // Fetch monthly ticket counts for line chart
  useEffect(() => {
    if (!userId) return;
    setMonthlyLoading(true);
    setMonthlyError(null);
    const controller = new AbortController();
    const fetchMonthly = async () => {
      try {
        const result = await getMonthlyTicketCounts(dashboardQueryParams, { signal: controller.signal });
        setMonthlyData(result);
      } catch (err: any) {
        if (err?.name === 'CanceledError') return;
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          setMonthlyError('Unauthorized to load monthly counts. Please sign in again.');
        } else {
          setMonthlyError(err?.response?.data?.message || err?.message || 'Failed to load monthly counts');
        }
      } finally {
        setMonthlyLoading(false);
      }
    };
    fetchMonthly();
    return () => controller.abort();
  }, [userId, dashboardQueryParams]);


  const {
    data: completedData,
    loading: completedLoading,
    hasTimedOut: completedTimedOut
  } = usePieChart({
    ...dashboardQueryParams,
    category: "completed"
  });

  const {
    data: pendingData,
    loading: pendingLoading,
    hasTimedOut: pendingTimedOut
  } = usePieChart({
    ...dashboardQueryParams,
    category: "pending"
  });

  const { 
    data: completionData
  } = useAverageDaysCompletion({
    year: dateFilter.mode === 'year' ? dateFilter.year ?? undefined : undefined,
    sinceDate: dateFilter.mode === 'custom' ? dateFilter.sinceDate ?? undefined : undefined,
    untilDate: dateFilter.mode === 'custom' ? dateFilter.untilDate ?? undefined : undefined,
  })

  useEffect(() => {
    const chartTimer = setTimeout(() => setLoading(false), 2000);
    const statusTimer = setTimeout(() => setChartsReady(true), 4000);
    return () => {
      clearTimeout(chartTimer);
      clearTimeout(statusTimer);
    };
  }, []);

  const concernLabels = useMemo(
    () => ['Appliance', 'Building', 'Collateral', 'Exterior', 'Interior'],
    [],
  );

  return (
  <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#EFF3FD', p: 2 }}>
    {/* Filter by date */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2 }}>
      {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'grid', width: '100%', gap: 1 }}> */}
      <Box
        sx={{
          mt: 1,
          ml: 1,
          fontSize: 14,
          fontWeight: 500,
          width: '100%',
        }}
      >
        {formattedDateRange}
      </Box>
      <FilterButton
        onClick={() => setFilterModalOpen(true)}
        icon={FilterIcon}
        width={16}
        height={16}
      />
      {/* </Box> */}
    </Box>
      {/* Top row: Status cards + line chart */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} lg={4}>
          <DBStatusCards chartsReady={chartsReady} stats={stats ?? undefined} loading={statsLoading} />
          {statsError && (
            <Box sx={{ mt: 1, p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: 2 }}>
              <p style={{ margin: 0 }}>Error loading status cards: {statsError}</p>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} lg={8}>
          <AgingConcernLineChart
            loading={monthlyLoading || loading}
            data={monthlyData}
          />
          {monthlyError && (
            <Box sx={{ mt: 1, p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: 2 }}>
              <p style={{ margin: 0 }}>Error loading monthly chart: {monthlyError}</p>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Bottom row: Bar chart + 2 pie charts */}
      <Grid container spacing={2}>
        {/* Bar chart */}
        <Grid item xs={12} lg={4}>
          <AverageDayOfCompletionBar
            title="Average Days of Completion"
            loading={loading}
            subtitle="Type of Concern"
            data={completionData}

          />
        </Grid>

        {/* Pie chart 1 - Completed Tickets */}
        <Grid item xs={12} lg={4}>
          <AverageTicketsPie
            title="Completed Tickets"
            subtitle="Type of Concern"
            loading={completedLoading || loading}
            data={completedData}
            legendLabels={concernLabels}
            hasTimedOut={completedTimedOut}
          />
        </Grid>

        {/* Pie chart 2 - Pending Tickets */}
        <Grid item xs={12} lg={4}>
          <AverageTicketsPie
            title="Pending Tickets"
            subtitle="Type of Concern"
            loading={pendingLoading || loading}
            data={pendingData}
            legendLabels={concernLabels}
            hasTimedOut={pendingTimedOut}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        {/* <button onClick={handleSubmit}>Submit</button> */}
      </Box>
      {/* Filter Modal */}
      <DashboardFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        initialFilters={dateFilter}
        onApplyFilter={handleApplyFilter}
      />
    </Box>
  );
};	